import {FC, useState} from "react";
import {Zap, Clock, Shield, CheckCircle, AlertCircle, X} from "lucide-react";
import Button from "@/components/ui/Button/index";
import {ShredResponse} from "@/services/MicroServiceService";

interface ServiceAssemblyStepProps {
    shredsArr: ShredResponse[];
}

const ServiceAssemblyStep: FC<ServiceAssemblyStepProps> = ({shredsArr}) => {
    const [showMaintenanceTooltip, setShowMaintenanceTooltip] = useState(false);
    
    // Check if all shreds are completed (100% progress)
    const allShredsCompleted = shredsArr.length > 0 && shredsArr.every(shred => shred.progress >= 100);
    const completedCount = shredsArr.filter(shred => shred.progress >= 100).length;
    const totalCount = shredsArr.length;

    const getStepStatus = () => {
        if (shredsArr.length === 0) {
            return {
                icon: <Clock className="w-4 h-4"/>,
                text: "NO SHREDS",
                bgColor: "bg-gray-100",
                textColor: "text-gray-600",
                borderColor: "border-gray-300",
                headerBg: "bg-gray-50 border-gray-200"
            };
        }

        if (!allShredsCompleted) {
            return {
                icon: <Shield className="w-4 h-4"/>,
                text: "SHREDS INCOMPLETE",
                bgColor: "bg-yellow-100",
                textColor: "text-yellow-800",
                borderColor: "border-yellow-300",
                headerBg: "bg-yellow-50 border-yellow-200"
            };
        }

        return {
            icon: <CheckCircle className="w-4 h-4"/>,
            text: "READY TO ASSEMBLE",
            bgColor: "bg-green-100",
            textColor: "text-green-800",
            borderColor: "border-purple-200",
            headerBg: "bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200"
        };
    };

    const stepStatus = getStepStatus();
    const isStepDisabled = !allShredsCompleted;

    return (
        <div className={`bg-white rounded-xl md:rounded-2xl border shadow-sm overflow-hidden ${
            isStepDisabled ? 'opacity-60' : ''
        } ${stepStatus.borderColor}`}>
            <div className={`px-4 md:px-6 py-3 md:py-4 border-b ${stepStatus.headerBg}`}>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
                    <div className="flex items-center space-x-3 md:space-x-4">
                        <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center text-white shadow-lg ${
                            isStepDisabled
                                ? 'bg-gray-400'
                                : 'bg-gradient-to-br from-purple-500 to-indigo-600'
                        }`}>
                            {isStepDisabled ? <Shield className="w-5 h-5 md:w-6 md:h-6"/> : <Zap className="w-5 h-5 md:w-6 md:h-6"/>}
                        </div>
                        <div>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 gap-1 sm:gap-0">
                                <span className={`px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-bold w-fit ${
                                    isStepDisabled
                                        ? 'bg-gray-100 text-gray-600'
                                        : 'bg-purple-100 text-purple-800'
                                }`}>
                                    STEP 3
                                </span>
                                <h3 className="text-lg md:text-xl font-bold text-gray-900">Service Assembly</h3>
                            </div>
                            <p className="text-gray-600 mt-1 text-sm md:text-base">
                                {isStepDisabled
                                    ? "Complete all shreds to unlock service assembly"
                                    : "Combine your shreds into a unified, production-ready service"
                                }
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col items-start md:items-end">
                        <div className={`flex items-center space-x-1 md:space-x-2 px-2 md:px-3 py-1 md:py-2 ${stepStatus.bgColor} ${stepStatus.textColor} rounded-lg`}>
                            {stepStatus.icon}
                            <span className="text-xs md:text-sm font-medium">{stepStatus.text}</span>
                        </div>
                        {!allShredsCompleted && shredsArr.length > 0 && (
                            <p className="text-xs text-yellow-600 mt-1 md:text-right">
                                {completedCount}/{totalCount} shreds completed
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <div className="p-4 md:p-8">
                {isStepDisabled ? (
                    <div className={`text-center py-6 md:py-8 px-3 md:px-4 rounded-xl border-2 border-dashed ${
                        shredsArr.length === 0 
                            ? 'bg-gray-50/50 border-gray-200' 
                            : 'bg-yellow-50/50 border-yellow-200'
                    }`}>
                        <Shield className="w-12 h-12 md:w-16 md:h-16 text-gray-400 mx-auto mb-3 md:mb-4"/>
                        <h4 className="text-base md:text-lg font-semibold text-gray-600 mb-2">
                            {shredsArr.length === 0 ? 'No Shreds Available' : 'Shreds Incomplete'}
                        </h4>
                        <p className="text-gray-500 text-xs md:text-sm mb-3 md:mb-4 max-w-md mx-auto px-2">
                            {shredsArr.length === 0
                                ? 'Generate and complete shreds first before assembling your service'
                                : `Complete all ${totalCount} shreds (currently ${completedCount}/${totalCount} done) before proceeding to service assembly`
                            }
                        </p>
                        <div className="inline-flex items-center px-3 md:px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-xs md:text-sm font-medium">
                            <div className="w-2 h-2 bg-gray-400 rounded-full mr-2"></div>
                            Waiting for completion
                        </div>
                    </div>
                ) : (
                    <div className="text-center">
                        <div className="max-w-md mx-auto px-4">
                            <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-3 md:mb-4 shadow-lg">
                                <Zap className="w-6 h-6 md:w-8 md:h-8 text-white"/>
                            </div>
                            <h4 className="text-lg md:text-xl font-bold text-gray-900 mb-2">Ready to Assemble</h4>
                            <p className="text-gray-600 mb-4 md:mb-6 text-sm md:text-base">
                                All {totalCount} shred{totalCount > 1 ? 's are' : ' is'} complete!
                                Combine them into a unified, deployable service.
                            </p>

                            {/* Progress indicator */}
                            <div className="bg-green-50 rounded-xl p-3 md:p-4 mb-4 md:mb-6 border border-green-200">
                                <div className="flex items-center justify-center space-x-2 mb-2">
                                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-green-600"/>
                                    <span className="text-green-800 font-medium text-sm md:text-base">All Shreds Completed</span>
                                </div>
                                <div className="w-full bg-green-200 rounded-full h-2">
                                    <div className="bg-green-500 h-2 rounded-full w-full transition-all duration-500"></div>
                                </div>
                                <p className="text-xs text-green-600 mt-2">{completedCount}/{totalCount} shreds ready</p>
                            </div>

                            <div className="flex justify-center relative">
                                <Button
                                    variant="solid"
                                    className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-6 md:px-10 py-3 md:py-4 text-base md:text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 whitespace-normal flex items-center justify-center"
                                    disabled={isStepDisabled}
                                    onClick={() => setShowMaintenanceTooltip(true)}
                                >
                                    <Zap className="w-4 h-4 md:w-5 md:h-5 mr-2 flex-shrink-0"/>
                                    <span>ASSEMBLE TO SERVICE</span>
                                </Button>

                                {/* Maintenance Tooltip */}
                                {showMaintenanceTooltip && (
                                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                                        <div className="bg-white rounded-xl md:rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                                            <div className="bg-gradient-to-r from-orange-500 to-red-500 px-4 md:px-6 py-3 md:py-4 text-white rounded-t-xl md:rounded-t-2xl">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-2 md:space-x-3">
                                                        <AlertCircle className="w-5 h-5 md:w-6 md:h-6"/>
                                                        <h3 className="text-lg md:text-xl font-bold">Feature Under Maintenance</h3>
                                                    </div>
                                                    <button 
                                                        onClick={() => setShowMaintenanceTooltip(false)}
                                                        className="text-white hover:text-gray-200 transition-colors"
                                                    >
                                                        <X className="w-5 h-5 md:w-6 md:h-6"/>
                                                    </button>
                                                </div>
                                            </div>
                                            
                                            <div className="p-4 md:p-6">
                                                <p className="text-gray-700 mb-4 md:mb-6 text-center text-sm md:text-base">
                                                    This feature is under maintenance and will be back soon. But you can still assemble your service manually by following these steps:
                                                </p>
                                                
                                                <div className="space-y-3 text-left">
                                                    <div className="bg-gray-50 rounded-lg p-3 md:p-4 border border-gray-200">
                                                        <h4 className="font-semibold text-gray-900 mb-2 text-sm md:text-base">Manual Assembly Steps:</h4>
                                                        <ol className="text-xs md:text-sm text-gray-700 space-y-2">
                                                            <li>1. Merge all shred branches into main</li>
                                                            <li>2. Create root <code className="bg-gray-200 px-1 rounded text-xs">pom.xml</code> with modules list</li>
                                                            <li>3. Move dependencies from module POMs to root POM</li>
                                                            <li>4. Update module POMs with parent reference only</li>
                                                            <li>5. Run <code className="bg-gray-200 px-1 rounded text-xs">mvn clean package</code></li>
                                                        </ol>
                                                    </div>
                                                </div>

                                                <div className="mt-4 md:mt-6 text-center">
                                                    <button 
                                                        onClick={() => setShowMaintenanceTooltip(false)}
                                                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 md:px-6 py-2 rounded-lg font-medium transition-colors text-sm md:text-base"
                                                    >
                                                        Got it, thanks!
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ServiceAssemblyStep;